import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import LocalStorageService from '../../../utility/LocalStorageService';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';

const selectFilteredDeviceList = createSelector([
  (state) => state.filteredDeviceList.deviceList], (deviceList) => deviceList);

const selectDeviceListPageNo = createSelector([
  (state) => state.filteredDeviceList.currPage], (currPage) => currPage);

const selectDeviceListTotalResults = createSelector([
  (state) => state.filteredDeviceList.totalNumRecords], (totalNumRecords) => totalNumRecords);

const selectAllIds = createSelector([
  (state) => state.filteredDeviceList.totalRecordsIdList],
(totalRecordsIdList) => totalRecordsIdList);

const selectDeviceListRowsPerPage = createSelector([
  (state) => state.filteredDeviceList,
], (deviceList) => deviceList.rowsPerPage);

const selectDeviceListColumnVisibility = createSelector([
  (state) => state.filteredDeviceList,
], (deviceList) => deviceList.columnVisibility);

const selectDeviceListCrudStatus = (state) => state.filteredDeviceList.crudStatus;

const selectDeviceListActiveSearchParams = createSelector([
  (state) => state.filteredDeviceList,
], (deviceList) => deviceList.activeSearchParams);

const selectDeviceListHasNextPage = createSelector([
  (state) => state.filteredDeviceList,
], (deviceList) => deviceList.hasNextPage);

const selectDevicesByDeviceIds = (deviceIds) => createSelector([
  (state) => state.filteredDeviceList.deviceList],
(deviceList) => deviceList.filter((device) => deviceIds.includes(device.id)));

const selectIsDeviceListLocalStorageLoaded = createSelector(
  (state) => state.filteredDeviceList,
  (deviceList) => deviceList.isLocalStorageLoaded,
);

const fetchFilteredDeviceList = createAsyncThunk(
  'securedApi/deviceList/fetchFilteredDeviceList',
  async (searchRequest, { rejectWithValue, getState }) => {
    try {
      const { rowsPerPage, currPage } = getState().filteredDeviceList;
      const response = await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.apiUrl}/api/v1/search/device`,
          { data: { ...searchRequest, pageNo: currPage, pageSize: rowsPerPage } });
      return {
        ...response,
        searchRequest,
      };
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const refreshFilteredDeviceList = createAsyncThunk(
  'securedApi/deviceList/refreshFilteredDeviceList',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { rowsPerPage, currPage, activeSearchParams } = getState().filteredDeviceList;
      return await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.apiUrl}/api/v1/search/device`,
          { data: { ...activeSearchParams, pageNo: currPage, pageSize: rowsPerPage } });
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const initialState = {
  deviceList: [],
  currPage: 0,
  hasNextPage: false,
  totalNumRecords: 0,
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  error: {},
  activeSearchParams: {
    id: null,
    name: null,
    filters: [],
    sort: null,
    sortType: null,
  },
  columnVisibility: {
    'Push Status': true,
    'Device Name': true,
    Tags: true,
    Model: true,
    'MAC Address': true,
    'Last Message': true,
    'Room Alert Link': true,
    Firmware: true,
  },
  rowsPerPage: 50,
  isLocalStorageLoaded: false,
  version: LocalStorageService.VERSION,
  totalRecordsIdList: [],
  suspendedDeviceIdList: [],
};

const deviceListSlice = createSlice({
  name: 'filteredDeviceList',
  initialState,
  reducers: {
    setDeviceListRowsPerPage: (state, action) => {
      let rowsPerPage = action.payload;
      if (rowsPerPage > 250) {
        rowsPerPage = 250;
      } else if (rowsPerPage < 1) {
        rowsPerPage = 1;
      }
      state.rowsPerPage = rowsPerPage;
      state.currPage = 0;
      LocalStorageService.saveStateToLocalStorage(LocalStorageService.DEVICE_LIST_KEY, {
        activeSearchParams: state.activeSearchParams,
        rowsPerPage: state.rowsPerPage,
        columnVisibility: state.columnVisibility,
        version: LocalStorageService.VERSION,
      });
    },
    setDeviceListColumnVisibility: (state, action) => {
      const { columnName, visible } = action.payload;
      state.columnVisibility[columnName] = visible;
      LocalStorageService.saveStateToLocalStorage(LocalStorageService.DEVICE_LIST_KEY, {
        activeSearchParams: state.activeSearchParams,
        rowsPerPage: state.rowsPerPage,
        columnVisibility: state.columnVisibility,
        version: LocalStorageService.VERSION,
      });
    },
    setDeviceListPage: (state, action) => {
      const pageNo = action.payload;
      state.currPage = pageNo < 0 ? 0 : pageNo;
    },
    setAppliedDeviceSearch: (state, action) => {
      state.activeSearchParams = {
        id: action.payload.id || state.activeSearchParams.id || null,
        name: action.payload.name || state.activeSearchParams.name || null,
        filters: [...action.payload.filters],
        sort: state.activeSearchParams.sort || null,
        sortType: state.activeSearchParams.sortType || null,
      };
      LocalStorageService.saveStateToLocalStorage(LocalStorageService.DEVICE_LIST_KEY, {
        activeSearchParams: state.activeSearchParams,
        rowsPerPage: state.rowsPerPage,
        columnVisibility: state.columnVisibility,
        version: LocalStorageService.VERSION,
      });
    },
    clearAppliedDeviceSearch: (state) => {
      state.activeSearchParams = {
        id: null,
        name: null,
        filters: [],
        sort: state.activeSearchParams.sort || null,
        sortType: state.activeSearchParams.sortType || null,
      };
      LocalStorageService.saveStateToLocalStorage(LocalStorageService.DEVICE_LIST_KEY, {
        activeSearchParams: state.activeSearchParams,
        rowsPerPage: state.rowsPerPage,
        columnVisibility: state.columnVisibility,
        version: LocalStorageService.VERSION,
      });
    },
    clearDeviceListSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase('LOAD_LOCAL_USER_STORAGE', (state) => {
        const localStorageState = LocalStorageService
          .getStateFromLocalStorage(LocalStorageService.DEVICE_LIST_KEY);
        if (localStorageState && localStorageState.version === LocalStorageService.VERSION) {
          state.rowsPerPage = localStorageState.rowsPerPage || initialState.rowsPerPage;
          state.columnVisibility = localStorageState.columnVisibility
            || initialState.columnVisibility;
          // eslint-disable-next-line max-len
          state.activeSearchParams = localStorageState.activeSearchParams || initialState.activeSearchParams;
        } else {
          localStorage.removeItem(LocalStorageService.DEVICE_LIST_KEY);
        }
        state.isLocalStorageLoaded = true;
      })
      .addCase(fetchFilteredDeviceList.pending, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(fetchFilteredDeviceList.fulfilled, (state, action) => {
        state.crudStatus = CrudStatus.SUCCESS;
        state.deviceList = action.payload.searchResult;

        if (!state.activeSearchParams.sort && !state.activeSearchParams.sortType) {
          state.deviceList.sort((a, b) => {
            if (a.suspended === true) return 1;
            if (b.suspended === true) return -1;
            return 0;
          });
        }

        state.totalNumRecords = action.payload.pageInfo.totalRecords;
        state.totalRecordsIdList = action.payload.totalRecordsIdList;
        state.hasNextPage = action.payload.pageInfo.hasNext;
        state.crudStatus = CrudStatus.IDLE;
      })
      .addCase(fetchFilteredDeviceList.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.error = [action.payload];
      })
      .addCase(refreshFilteredDeviceList.pending, (state) => {
        state.crudOperation = CrudType.BACKGROUND_READ;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(refreshFilteredDeviceList.fulfilled, (state, action) => {
        state.crudStatus = CrudStatus.SUCCESS;
        state.deviceList = action.payload.searchResult;

        if (!state.activeSearchParams.sort && !state.activeSearchParams.sortType) {
          state.deviceList.sort((a, b) => {
            if (a.suspended === true) return 1;
            if (b.suspended === true) return -1;
            return 0;
          });
        }

        state.totalNumRecords = action.payload.pageInfo.totalRecords;
        state.hasNextPage = action.payload.pageInfo.hasNext;
        state.crudStatus = CrudStatus.IDLE;
      })
      .addCase(refreshFilteredDeviceList.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.error = [action.payload];
      });
  },
});

const { actions, reducer } = deviceListSlice;

export const {
  setDeviceListColumnVisibility,
  setDeviceListRowsPerPage,
  clearDeviceListSlice,
  setDeviceListPage,
  setAppliedDeviceSearch,
  clearAppliedDeviceSearch,
} = actions;
export {
  selectDeviceListColumnVisibility,
  selectDeviceListRowsPerPage,
  refreshFilteredDeviceList,
  selectFilteredDeviceList,
  selectDeviceListPageNo,
  selectDeviceListTotalResults,
  fetchFilteredDeviceList,
  selectDeviceListCrudStatus,
  selectDeviceListActiveSearchParams,
  selectDeviceListHasNextPage,
  selectDevicesByDeviceIds,
  selectIsDeviceListLocalStorageLoaded,
  selectAllIds,
};
export default reducer;
